import { CO_SIGNING_MAX_SIGNERS } from "@/constants/settings";

import type {
  ApiUser,
  CoSigningStatus,
  MultisigActiveSigner,
  MultisigSignerRole,
  UserRole,
} from "@/types";

export type CoSignerRowState = "approved" | "pending" | "invited";

export type CoSignerRowInfo = {
  state: CoSignerRowState;
  role: MultisigSignerRole;
  publicKey?: string;
};

/**
 * Resolve the co-signer state for a given tenant user, matched by id.
 * Returns `null` when the user is not associated with the multisig quorum.
 *
 * Precedence (mirrors backend intent): `active` > `pending` > `invited`.
 * `pending_signer` intentionally does not expose a public key here — that
 * signer has not finished key setup yet.
 */
export const getCoSignerRowInfo = (
  userId: string,
  status: CoSigningStatus | undefined,
): CoSignerRowInfo | null => {
  const org = status?.org;
  if (!org) return null;

  const active = org.active_signers.find((s) => s.user_id === userId);
  if (active) {
    return { state: "approved", role: active.role, publicKey: active.stellar_public_key };
  }

  if (org.pending_signer?.user_id === userId) {
    return {
      state: "pending",
      role: org.pending_signer.role,
      publicKey: org.pending_signer.stellar_public_key,
    };
  }

  const invited = (org.invited_signers ?? []).find(
    (s: MultisigActiveSigner) => s.user_id === userId,
  );
  if (invited) {
    return { state: "invited", role: invited.role, publicKey: invited.stellar_public_key };
  }

  return null;
};

/**
 * Approved-only count. Pending / Invited signers do not hold a slot until the
 * governance action is signed, so this is what the "X of 19 co-signers"
 * display should track.
 */
export const getApprovedCoSignerCount = (status: CoSigningStatus | undefined): number =>
  status?.org.active_signers.length ?? 0;

/**
 * Reserved-count for the `CO_SIGNING_MAX_SIGNERS` ceiling: active signers +
 * the current pending signer (if any) + invited signers. Every invited row
 * will eventually flow through pending → active, so we must count them at
 * invite time — otherwise multiple concurrent invites can slip past the
 * approved-only check and get approved one-by-one past the max (see
 * `approvePendingSigner` guard). Distinct from `getApprovedCoSignerCount`,
 * which is what the "X of 19 co-signers" display shows.
 */
export const getReservedCoSignerCount = (status: CoSigningStatus | undefined): number => {
  if (!status) return 0;
  const active = status.org.active_signers.length;
  const pending = status.org.pending_signer ? 1 : 0;
  const invited = status.org.invited_signers?.length ?? 0;
  return active + pending + invited;
};

/**
 * Guard result for the "Remove co-signer" flow. `ok:false` when removing the
 * given user would violate the two-Owner minimum required to sign the next
 * governance action. Callers use `reason` verbatim as the inline explanation
 * shown next to the disabled menu item (matches the prototype copy).
 */
export type RemoveGuard = { ok: true } | { ok: false; reason: string };

const TWO_ACTIVE_OWNER_REASON =
  "Co-signing needs two active Owners. Add a third Owner as a co-signer first.";

const isSignerActive = (userId: string, users: ApiUser[] | undefined): boolean => {
  if (!users) return true;
  const user = users.find((u) => u.id === userId);
  return user ? user.is_active !== false : true;
};

/**
 * Can the given user be removed as a co-signer right now?
 *
 *  - The target must be a currently-active signer (returns `ok:false` with a
 *    generic reason otherwise; used defensively by the mutation).
 *  - If the target is an OWNER, the number of remaining OWNER co-signers whose
 *    tenant account is still `is_active` after the removal must be ≥ 2. A
 *    deactivated Owner co-signer therefore does not count toward the floor.
 *
 * Removing a Financial Controller is always allowed here — the max-19 slot
 * math is checked separately by the invite flow (Subtask D), not the remove
 * flow.
 */
export const canRemoveCoSigner = (
  userId: string,
  status: CoSigningStatus | undefined,
  users?: ApiUser[],
): RemoveGuard => {
  const active = status?.org.active_signers ?? [];
  const target = active.find((s) => s.user_id === userId);

  if (!target) {
    return { ok: false, reason: "This user is not an active co-signer." };
  }

  if (target.role !== "OWNER") {
    return { ok: true };
  }

  const remainingActiveOwners = active.filter(
    (s) => s.role === "OWNER" && s.user_id !== userId && isSignerActive(s.user_id, users),
  ).length;

  return remainingActiveOwners >= 2
    ? { ok: true }
    : { ok: false, reason: TWO_ACTIVE_OWNER_REASON };
};

/**
 * Guard result for the "Deactivate" flow. `ok:false` when deactivating the
 * target Owner co-signer would drop the number of **active** Owner co-signers
 * below the two-Owner floor required to sign the next governance action.
 *
 *  - Non-co-signer target → always ok. (Deactivation is a tenant-user concern
 *    when there is no signer row.)
 *  - Financial Controller co-signer → always ok. The two-Owner floor only
 *    counts Owner co-signers.
 *  - Owner co-signer → ok iff at least 2 **other** active Owner co-signers
 *    remain after the deactivation.
 *
 * Note: this floor is separate from removal. A deactivated Owner co-signer
 * still holds a signer slot (their key stays in the quorum). Owners can then
 * `Remove as co-signer` them separately, which runs its own signing-gated
 * flow.
 */
export type DeactivateGuard = { ok: true } | { ok: false; reason: string };

export const canDeactivateUser = (
  userId: string,
  status: CoSigningStatus | undefined,
  users?: ApiUser[],
): DeactivateGuard => {
  const active = status?.org.active_signers ?? [];
  const target = active.find((s) => s.user_id === userId);

  if (!target || target.role !== "OWNER") {
    return { ok: true };
  }

  const remainingActiveOwners = active.filter(
    (s) => s.role === "OWNER" && s.user_id !== userId && isSignerActive(s.user_id, users),
  ).length;

  return remainingActiveOwners >= 2
    ? { ok: true }
    : { ok: false, reason: TWO_ACTIVE_OWNER_REASON };
};

/**
 * Only Owner and Financial Controller team members can be co-signers. Every
 * other role (developer / business / initiator / approver) never gets a
 * co-signer entry in the kebab.
 */
export const isEligibleForCoSigning = (role: UserRole | null | undefined): boolean =>
  role === "owner" || role === "financial_controller";

/**
 * Guard result for the "Invite as co-signer" flow. `ok:false` when the
 * approved co-signer count already sits at the `CO_SIGNING_MAX_SIGNERS`
 * ceiling; callers surface `reason` verbatim as the inline explanation next
 * to the disabled menu item (matches the prototype copy).
 */
export type InviteGuard = { ok: true } | { ok: false; reason: string };

const buildLimitReason = (max: number) =>
  `You've reached the limit of ${max} co-signers. Remove one to add someone new.`;

/**
 * Can a not-yet-co-signer, eligible-role user be invited right now?
 *
 *  - Callers should first confirm `getCoSignerRowInfo(...) === null`
 *    (defensive `ok:false` returned if already a signer).
 *  - Blocked when the reserved count (active + pending + invited) already
 *    sits at the `CO_SIGNING_MAX_SIGNERS` ceiling; counting invited /
 *    pending is required because each of those rows will eventually be
 *    promoted to active, and the promotion path (`approvePendingSigner`)
 *    also enforces this same ceiling.
 *
 * Role eligibility (`isEligibleForCoSigning`) is intentionally the caller's
 * responsibility so the "Invite as co-signer" menu item is not shown at all
 * for ineligible roles.
 */
export const canInviteCoSigner = (
  userId: string,
  status: CoSigningStatus | undefined,
): InviteGuard => {
  if (getCoSignerRowInfo(userId, status) !== null) {
    return { ok: false, reason: "This user is already a co-signer." };
  }
  if (getReservedCoSignerCount(status) >= CO_SIGNING_MAX_SIGNERS) {
    return { ok: false, reason: buildLimitReason(CO_SIGNING_MAX_SIGNERS) };
  }
  return { ok: true };
};
